import Cookies from 'js-cookie';

const DEFAULT_EXPIRES = 50;

const WEB_PREFIX = '__WEB_STORAGE';
const LOCAL = 'LOCAL';
const SESSION = 'SESSION';

interface IWebStorage {
  getLocalStorage: GetLocalStorageType;
  setLocalStorage: SetLocalStorageType;
  removeLocalStorage: RemoveLocalStorageType;
  getSessionStorage: GetSessionStorageType;
  setSessionStorage: SetSessionStorageType;
  removeSessionStorage: RemoveSessionStorageType;
}

type GetLocalStorageType = (key: string) => any;
type SetLocalStorageType = (key: string, value: any, expires?: number) => void;
type RemoveLocalStorageType = (key: string) => void;
type GetSessionStorageType = (key: string) => any;
type SetSessionStorageType = (key: string, value: any, expires?: number) => void;
type RemoveSessionStorageType = (key: string) => void;

const WebStorage = (): IWebStorage => {
  // Check for support with localStorage
  const checkLocalStorageSupport = (): boolean => {
    let support = true;

    try {
      localStorage.setItem('storageTest', 'storageTest');
      localStorage.getItem('storageTest');
      localStorage.removeItem('storageTest');

      //throw 'error localStorage support';
    } catch (e) {
      support = false;
    }

    return support;
  };

  // Check for support with sessionStorage
  const checkSessionStorageSupport = (): boolean => {
    let support = true;

    try {
      sessionStorage.setItem('storageTest', 'storageTest');
      sessionStorage.getItem('storageTest');
      sessionStorage.removeItem('storageTest');

      //throw 'error sessionStorage support';
    } catch (e) {
      support = false;
    }

    return support;
  };

  const setCookie = (key: string, value: string, prefix: string, expires?: number): void => {
    Cookies.set(`${WEB_PREFIX}_${prefix}_${key}`, value, {
      expires: expires || DEFAULT_EXPIRES,
      sameSite: 'Strict',
      secure: true,
    });
  };

  const getCookie = (key: string, prefix: string): string | undefined => {
    return Cookies.get(`${WEB_PREFIX}_${prefix}_${key}`);
  };

  const removeCookie = (key: string, prefix: string): void => {
    Cookies.remove(`${WEB_PREFIX}_${prefix}_${key}`);
  };

  // Setup getters and setters to expose

  const getLocalStorage: GetLocalStorageType = (key) => {
    let output: string | undefined | null;

    if (checkLocalStorageSupport()) {
      output = localStorage.getItem(key);
    } else {
      output = getCookie(key, LOCAL);
    }

    if (output === 'undefined') {
      return undefined;
    }
    if (typeof output === 'string') {
      return JSON.parse(output);
    }
    return null;
  };

  const setLocalStorage: SetLocalStorageType = (key, value, expires) => {
    const strValue = JSON.stringify(value);

    if (checkLocalStorageSupport()) {
      localStorage.setItem(key, strValue);
    } else {
      setCookie(key, strValue, LOCAL, expires);
    }
  };

  const removeLocalStorage: RemoveLocalStorageType = (key) => {
    if (checkLocalStorageSupport()) {
      localStorage.removeItem(key);
    } else {
      removeCookie(key, LOCAL);
    }
  };

  const getSessionStorage: GetSessionStorageType = (key) => {
    let output: string | undefined | null;

    if (checkSessionStorageSupport()) {
      output = sessionStorage.getItem(key);
    } else {
      output = getCookie(key, SESSION);
    }

    if (output === 'undefined') {
      return undefined;
    }
    if (typeof output === 'string') {
      return JSON.parse(output);
    }
    return null;
  };

  const setSessionStorage: SetSessionStorageType = (key, value, expires) => {
    const strValue = JSON.stringify(value);

    if (checkSessionStorageSupport()) {
      sessionStorage.setItem(key, strValue);
    } else {
      setCookie(key, strValue, SESSION, expires);
    }
  };

  const removeSessionStorage: RemoveSessionStorageType = (key) => {
    if (checkSessionStorageSupport()) {
      sessionStorage.removeItem(key);
    } else {
      removeCookie(key, SESSION);
    }
  };

  return {
    getLocalStorage,
    setLocalStorage,
    removeLocalStorage,
    getSessionStorage,
    setSessionStorage,
    removeSessionStorage,
  };
};

export default WebStorage();
